import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Ticket, Plus, Edit, Trash2, CheckCircle, XCircle, 
  Search, Filter, Calendar, Users, DollarSign,
  TrendingUp, Clock, Tag, Percent, AlertCircle
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './components/ui/dialog';
import { Switch } from './components/ui/switch';
import { Label } from './components/ui/label';

import { getBackendUrl } from './utils/api';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const CouponsContent = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, inactive
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [stats, setStats] = useState({ active_coupons: 0, total_uses: 0, total_discount: 0 });
  
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 10,
    start_date: '',
    end_date: '',
    usage_limit: null,
    usage_per_customer: 1,
    first_purchase_only: false,
    minimum_order_value: 0,
    is_active: true
  });

  useEffect(() => {
    loadCoupons();
    loadStats();
  }, []);

  const loadCoupons = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/coupons`);
      setCoupons(response.data);
    } catch (err) {
      console.error('Error loading coupons:', err);
      setError('Erro ao carregar cupons');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API}/coupons/stats/summary`);
      setStats(response.data);
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError('');
      setSuccess('');

      if (!formData.code.trim()) {
        setError('Código do cupom é obrigatório');
        return;
      }

      if (editingCoupon) {
        await axios.put(`${API}/coupons/${editingCoupon.id}`, formData);
        setSuccess('Cupom atualizado com sucesso!');
      } else {
        await axios.post(`${API}/coupons`, formData);
        setSuccess('Cupom criado com sucesso!');
      }

      setTimeout(() => setSuccess(''), 3000);
      setIsModalOpen(false);
      loadCoupons();
      loadStats();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao salvar cupom');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (couponId) => {
    if (!window.confirm('Tem certeza que deseja excluir este cupom?')) return;

    try {
      await axios.delete(`${API}/coupons/${couponId}`);
      setSuccess('Cupom excluído com sucesso!');
      setTimeout(() => setSuccess(''), 3000);
      loadCoupons();
      loadStats();
    } catch (err) {
      setError('Erro ao excluir cupom');
    }
  };

  const handleEdit = (coupon) => {
    setEditingCoupon(coupon);
    setFormData({
      code: coupon.code,
      description: coupon.description || '',
      discount_type: coupon.discount_type,
      discount_value: coupon.discount_value,
      start_date: coupon.start_date ? coupon.start_date.split('T')[0] : '',
      end_date: coupon.end_date ? coupon.end_date.split('T')[0] : '',
      usage_limit: coupon.usage_limit,
      usage_per_customer: coupon.usage_per_customer || 1,
      first_purchase_only: coupon.first_purchase_only || false,
      minimum_order_value: coupon.minimum_order_value || 0,
      is_active: coupon.is_active
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setEditingCoupon(null);
    setFormData({
      code: '',
      description: '',
      discount_type: 'percentage',
      discount_value: 10,
      start_date: '',
      end_date: '',
      usage_limit: null,
      usage_per_customer: 1,
      first_purchase_only: false,
      minimum_order_value: 0,
      is_active: true
    });
  };

  const filteredCoupons = coupons.filter(coupon => {
    const matchesSearch = !searchQuery || 
      coupon.code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (coupon.description && coupon.description.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && coupon.is_active) ||
      (filterStatus === 'inactive' && !coupon.is_active);
    
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (coupon) => {
    const now = new Date();
    const endDate = coupon.end_date ? new Date(coupon.end_date) : null;
    const usageLimit = coupon.usage_limit;
    const timesUsed = coupon.times_used || 0;

    if (!coupon.is_active) {
      return { text: 'Inativo', color: 'bg-red-500/20 text-red-400 border-red-500/40' };
    }
    if (endDate && endDate < now) {
      return { text: 'Expirado', color: 'bg-gray-500/20 text-gray-400 border-gray-500/40' };
    }
    if (usageLimit && timesUsed >= usageLimit) {
      return { text: 'Esgotado', color: 'bg-orange-500/20 text-orange-400 border-orange-500/40' };
    }
    if (endDate) {
      const daysUntilExpiry = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
      if (daysUntilExpiry <= 7) {
        return { text: 'Expirando', color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/40' };
      }
    }
    return { text: 'Ativo', color: 'bg-green-500/20 text-green-400 border-green-500/40' };
  };

  const getDiscountDisplay = (coupon) => {
    if (coupon.discount_type === 'percentage') {
      return (
        <div className="flex items-center gap-2 text-purple-400 font-bold text-xl">
          <Percent className="w-5 h-5" />
          {coupon.discount_value}% OFF
        </div>
      );
    } else {
      return (
        <div className="flex items-center gap-2 text-blue-400 font-bold text-xl">
          <DollarSign className="w-5 h-5" />
          R$ {coupon.discount_value.toFixed(2)} OFF
        </div>
      );
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">🎫 Cupons de Desconto</h2>
          <p className="text-gray-400">Crie cupons promocionais para atrair e fidelizar clientes</p>
        </div>
      </div>

      {/* Alerts */}
      {success && (
        <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 flex items-center gap-3">
          <CheckCircle className="text-green-500" size={20} />
          <span className="text-green-400">{success}</span>
        </div>
      )}

      {error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 flex items-center gap-3">
          <XCircle className="text-red-500" size={20} />
          <span className="text-red-400">{error}</span>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-green-500/10 to-emerald-600/5 border border-green-500/20 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-500/20 rounded-lg flex items-center justify-center">
              <Ticket className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Cupons Ativos</p>
              <p className="text-white text-2xl font-bold">{stats.active_coupons}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-blue-500/10 to-cyan-600/5 border border-blue-500/20 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-500/20 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total de Usos</p>
              <p className="text-white text-2xl font-bold">{stats.total_uses}</p>
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500/10 to-pink-600/5 border border-purple-500/20 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-purple-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total em Descontos</p>
              <p className="text-white text-2xl font-bold">R$ {stats.total_discount.toFixed(2)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search and Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={() => {
            resetForm();
            setIsModalOpen(true);
          }}
          className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl transition-all font-semibold flex items-center gap-2 shadow-lg shadow-purple-500/30"
        >
          <Plus className="w-5 h-5" />
          Novo Cupom
        </button>

        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Buscar cupom por código..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-11 pr-4 py-2 bg-slate-800/50 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setFilterStatus('all')}
            className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${
              filterStatus === 'all'
                ? 'bg-purple-500 text-white'
                : 'bg-slate-800/50 text-gray-400 hover:text-white'
            }`}
          >
            Todos
          </button>
          <button
            onClick={() => setFilterStatus('active')}
            className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${
              filterStatus === 'active'
                ? 'bg-green-500 text-white'
                : 'bg-slate-800/50 text-gray-400 hover:text-white'
            }`}
          >
            Ativos
          </button>
          <button
            onClick={() => setFilterStatus('inactive')}
            className={`px-4 py-2 rounded-xl font-medium text-sm transition-all ${
              filterStatus === 'inactive'
                ? 'bg-red-500 text-white'
                : 'bg-slate-800/50 text-gray-400 hover:text-white'
            }`}
          >
            Inativos
          </button>
        </div>
      </div>

      {/* Coupons Grid */}
      {loading && coupons.length === 0 ? (
        <div className="text-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto"></div>
          <p className="text-gray-400 mt-4">Carregando cupons...</p>
        </div>
      ) : filteredCoupons.length === 0 ? (
        <div className="text-center py-20">
          <Ticket className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h3 className="text-white font-bold text-xl mb-2">Nenhum cupom encontrado</h3>
          <p className="text-gray-400 mb-6">
            {searchQuery ? 'Tente outro termo de busca' : 'Crie seu primeiro cupom para começar'}
          </p>
          {!searchQuery && (
            <button
              onClick={() => {
                resetForm();
                setIsModalOpen(true);
              }}
              className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl transition-all font-semibold shadow-lg shadow-purple-500/30"
            >
              Criar Primeiro Cupom
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCoupons.map((coupon) => {
            const status = getStatusBadge(coupon);
            const usagePercent = coupon.usage_limit 
              ? Math.min((coupon.times_used / coupon.usage_limit) * 100, 100)
              : 0;

            return (
              <div
                key={coupon.id}
                className="bg-gradient-to-br from-slate-800/50 to-slate-900/30 backdrop-blur-sm border border-gray-700/50 rounded-xl p-5 hover:border-purple-500/30 transition-all group"
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Ticket className="w-5 h-5 text-purple-400" />
                      <h3 className="text-white font-bold text-lg break-all">{coupon.code}</h3>
                    </div>
                    <span className={`inline-block px-3 py-1 rounded-lg text-xs font-bold border ${status.color}`}>
                      {status.text}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDelete(coupon.id)}
                    className="opacity-0 group-hover:opacity-100 p-2 hover:bg-red-500/20 rounded-lg transition-all"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>

                {/* Description */}
                {coupon.description && (
                  <p className="text-gray-400 text-sm mb-4">{coupon.description}</p>
                )}

                {/* Discount */}
                <div className="mb-4">
                  {getDiscountDisplay(coupon)}
                </div>

                {/* Details */}
                <div className="space-y-2 text-sm">
                  {coupon.end_date && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <Calendar className="w-4 h-4" />
                      <span>Válido até {new Date(coupon.end_date).toLocaleDateString('pt-BR')}</span>
                    </div>
                  )}
                  
                  {coupon.usage_limit && (
                    <div>
                      <div className="flex items-center justify-between text-gray-400 mb-1">
                        <div className="flex items-center gap-2">
                          <Tag className="w-4 h-4" />
                          <span>{coupon.times_used}/{coupon.usage_limit} usos</span>
                        </div>
                        <span>{usagePercent.toFixed(0)}%</span>
                      </div>
                      <div className="w-full bg-gray-700 rounded-full h-2">
                        <div 
                          className="bg-gradient-to-r from-purple-500 to-pink-500 h-2 rounded-full transition-all"
                          style={{ width: `${usagePercent}%` }}
                        ></div>
                      </div>
                    </div>
                  )}

                  {coupon.minimum_order_value > 0 && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <DollarSign className="w-4 h-4" />
                      <span>Mínimo: R$ {coupon.minimum_order_value.toFixed(2)}</span>
                    </div>
                  )}

                  {coupon.usage_per_customer && (
                    <div className="flex items-center gap-2 text-gray-400">
                      <Users className="w-4 h-4" />
                      <span>{coupon.usage_per_customer} uso(s) por cliente</span>
                    </div>
                  )}

                  {coupon.first_purchase_only && (
                    <div className="flex items-center gap-2 text-yellow-400 text-xs">
                      <AlertCircle className="w-4 h-4" />
                      <span>Apenas primeira compra</span>
                    </div>
                  )}
                </div>

                {/* Edit Button */}
                <button
                  onClick={() => handleEdit(coupon)}
                  className="w-full mt-4 py-2 bg-purple-500/20 hover:bg-purple-500/30 border border-purple-500/40 text-purple-400 rounded-lg font-medium transition-all flex items-center justify-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Editar
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal Create/Edit */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="bg-slate-900 border-gray-700 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-2xl">
              {editingCoupon ? '✏️ Editar Cupom' : '✨ Criar Novo Cupom'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Code and Description */}
            <div className="space-y-4">
              <div>
                <Label className="text-white font-semibold mb-2 block">Código do Cupom *</Label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="PRIMEIRACOMPRA"
                  className="w-full px-4 py-2 bg-slate-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500 uppercase"
                  maxLength={30}
                />
                <p className="text-gray-500 text-xs mt-1">Letras maiúsculas, sem espaços</p>
              </div>

              <div>
                <Label className="text-white font-semibold mb-2 block">Descrição</Label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="10% de desconto na primeira compra"
                  className="w-full px-4 py-2 bg-slate-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>

            {/* Discount Type and Value */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white font-semibold mb-2 block">Tipo de Desconto</Label>
                <select
                  value={formData.discount_type}
                  onChange={(e) => setFormData({ ...formData, discount_type: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-purple-500"
                >
                  <option value="percentage">Porcentagem (%)</option>
                  <option value="fixed">Valor Fixo (R$)</option>
                </select>
              </div>

              <div>
                <Label className="text-white font-semibold mb-2 block">Valor do Desconto</Label>
                <div className="relative">
                  {formData.discount_type === 'percentage' && (
                    <Percent className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
                  )}
                  {formData.discount_type === 'fixed' && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                  )}
                  <input
                    type="number"
                    value={formData.discount_value}
                    onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                    className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-purple-500"
                    min="0"
                    max={formData.discount_type === 'percentage' ? '100' : undefined}
                    step="0.01"
                  />
                </div>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white font-semibold mb-2 block">Data Início</Label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-purple-500"
                />
              </div>

              <div>
                <Label className="text-white font-semibold mb-2 block">Data Fim</Label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                  className="w-full px-4 py-2 bg-slate-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-purple-500"
                />
                <p className="text-gray-500 text-xs mt-1">Deixe vazio para sem data de término</p>
              </div>
            </div>

            {/* Usage Limits */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white font-semibold mb-2 block">Limite Total de Usos</Label>
                <input
                  type="number"
                  value={formData.usage_limit || ''}
                  onChange={(e) => setFormData({ ...formData, usage_limit: e.target.value ? parseInt(e.target.value) : null })}
                  placeholder="Ilimitado"
                  className="w-full px-4 py-2 bg-slate-800 border border-gray-700 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
                  min="0"
                />
              </div>

              <div>
                <Label className="text-white font-semibold mb-2 block">Limite por Cliente</Label>
                <input
                  type="number"
                  value={formData.usage_per_customer}
                  onChange={(e) => setFormData({ ...formData, usage_per_customer: parseInt(e.target.value) || 1 })}
                  className="w-full px-4 py-2 bg-slate-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  min="1"
                />
              </div>
            </div>

            {/* Minimum Order Value */}
            <div>
              <Label className="text-white font-semibold mb-2 block">Valor Mínimo do Pedido</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">R$</span>
                <input
                  type="number"
                  value={formData.minimum_order_value}
                  onChange={(e) => setFormData({ ...formData, minimum_order_value: parseFloat(e.target.value) || 0 })}
                  className="w-full pl-10 pr-4 py-2 bg-slate-800 border border-gray-700 rounded-xl text-white focus:outline-none focus:border-purple-500"
                  min="0"
                  step="0.01"
                />
              </div>
              <p className="text-gray-500 text-xs mt-1">Deixe 0 para sem valor mínimo</p>
            </div>

            {/* Toggles */}
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl">
                <div>
                  <Label className="text-white font-semibold">Apenas Primeira Compra</Label>
                  <p className="text-gray-400 text-sm">Cupom válido apenas para novos clientes</p>
                </div>
                <Switch
                  checked={formData.first_purchase_only}
                  onCheckedChange={(checked) => setFormData({ ...formData, first_purchase_only: checked })}
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-xl">
                <div>
                  <Label className="text-white font-semibold">Cupom Ativo</Label>
                  <p className="text-gray-400 text-sm">Ativar cupom imediatamente</p>
                </div>
                <Switch
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  resetForm();
                }}
                className="flex-1 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-white rounded-xl transition-all font-medium"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white rounded-xl transition-all font-semibold shadow-lg shadow-purple-500/30 disabled:opacity-50"
              >
                {loading ? 'Salvando...' : editingCoupon ? 'Atualizar' : 'Criar Cupom'}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CouponsContent;
